import React, { useEffect, useState } from "react";
import { Button, DownloadIcon } from "@pd/pd-ui-kit";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import Preloader from "~/components/Preloader";
import TableGen from "~/pages/IndicatorReports/TableGen";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData } from "~/hooks/locationData";
import { useSelectorDataConfigurator } from "~/hooks/selectorDataConfigurator";
import { handleIndicatorReportHash } from "~/pages/IndicatorReports/lib";
import { parseIndicatorReportToView } from "~/pages/IndicatorReports/ViewReport/lib";
import {
  DealerIndicatorValues,
  DealerIndicatorValuesReport,
  DealersRegMen,
  Indicator,
  IndicatorValue,
  RegMenInfo,
  TypeGroupDealerIndicatorValues,
  useGetIndicatorReportHashLazyQuery,
  useGetIndicatorReportToViewLazyQuery,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";

export interface IReportDealerCollection {
  dName: string;
  dValues: IndicatorValue[];
  dRegMans: DealersRegMen;
  dMeta: { [key: string]: string };
}

export interface IParsedIndicatorReport {
  dealersCollection: IReportDealerCollection[];
  indicatorsCollection: Indicator[];
  regMenCollection: RegMenInfo[];
  metadataCollection: { [key: string]: string };
}

const ViewReport = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { page, count, dateBegin, dateEnd } = useGetLocationData();
  // Ключи фильтра отчёта приходят из URL и не описаны в типе useGetLocationData
  const {
    DealerIds,
    HoldingIds,
    RegMenIds,
    FO,
    City,
    groupType,
    Region,
    quarter,
    year,
  } = useGetLocationData() as unknown as Record<string, string | undefined>;

  const { notifyReport } = useNotify();

  const [parsedReport, setParsedReport] = useState<IParsedIndicatorReport>();

  const getVariablesForRequests = () => ({
    ReportId: id,
    group: groupType as TypeGroupDealerIndicatorValues | undefined,
    page: page || 1,
    count,
    quarter: Number(quarter),
    year: Number(year),

    ...(FO && { FO: FO.split(",") }),
    ...(City && { City: City.split(",") }),
    ...(Region && { Region: Region.split(",") }),
    ...(DealerIds && { DealerIds: DealerIds.split(",") }),
    ...(HoldingIds && { HoldingIds: HoldingIds.split(",") }),
    ...(RegMenIds && { RegMenIds: RegMenIds.split(",") }),
    ...(dateBegin && { dateBegin }),
    ...(dateEnd && { dateEnd }),
  });

  const [getReport, getReportState] = useGetIndicatorReportToViewLazyQuery({
    fetchPolicy: "no-cache",
    variables: getVariablesForRequests(),
  });

  const reportInstance = get(
    getReportState,
    "data.IndicatorGroupReportGetter.getDealerIndicatorValuesReport",
  ) as DealerIndicatorValuesReport;

  // Get file hash
  const [getReportHash, getReportHashState] =
    useGetIndicatorReportHashLazyQuery({
      fetchPolicy: "cache-and-network",
      variables: getVariablesForRequests(),
    });

  const reportDealersItemsList = useSelectorDataConfigurator<
    DealerIndicatorValues,
    DealerIndicatorValues
  >({
    queryGenerator: useGetIndicatorReportToViewLazyQuery,
    responseDataGetterString:
      "IndicatorGroupReportGetter.getDealerIndicatorValuesReport.items",
    customDecoratedValueHandler: (element) => element,
    additionQueryParameters: getVariablesForRequests(),
    fetchPolicy: "no-cache",
    asLazy: true,
  });

  const metaDataValueDecorator = (key: string, val: any) => {
    switch (key) {
      case "status":
        return t(`dealers.${val}`);
      case "ladaBusiness":
      case "ladaSport":
        return val ? t("ui.filterPositive") : t("ui.filterNegative");
      default:
        return val;
    }
  };

  useEffect(() => {
    if (!!id) {
      getReport({ variables: { ReportId: id } });
      reportDealersItemsList.getter();
    }
  }, []);

  useEffect(() => {
    if (getReportHashState.data && !getReportHashState.loading) {
      const hash = handleIndicatorReportHash(getReportHashState);
      if (handleIndicatorReportHash(getReportHashState)) notifyReport(hash);
    }
  }, [getReportHashState.loading]);

  useEffect(() => {
    if (reportInstance) {
      const {
        IndicatorReport: { Fields },
      } = reportInstance;

      const dealerMetaInfoKeys = Fields.reduce(
        (acc, field) => ({ ...acc, [field.fieldName]: field.fieldRu }),
        {},
      );

      setParsedReport(
        parseIndicatorReportToView(
          reportInstance,
          reportDealersItemsList.rowsForSelect,
          dealerMetaInfoKeys,
        ),
      );
    }
  }, [reportInstance, reportDealersItemsList.rowsForSelect]);

  return (
    <Wrapper.Content
      title={t("reports.previewTitle")}
      controlComponent={
        <Button
          size={"middle"}
          icon={<DownloadIcon size={16} />}
          onClick={() => getReportHash()}
          disabled={!reportInstance}
          loading={getReportHashState.loading}
        >
          {t("ui.downloadFile")}
        </Button>
      }
    >
      <div style={{ marginTop: 10 }}>
        {!reportInstance ? (
          <Preloader />
        ) : (
          <TableGen
            parsedReport={parsedReport}
            paginationTrigger={reportDealersItemsList.introspectionHandler}
            paginationLoading={reportDealersItemsList.dataRequestLoading}
            metaDataValueDecorator={metaDataValueDecorator}
          />
        )}
      </div>
    </Wrapper.Content>
  );
};

export default ViewReport;
